function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }

    setTimeout(() => {
      productCardsSizes();
    }, 1000);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('.details');

    this.querySelector('#MenuDrawer-Overlay').addEventListener('click', () => this.close());
    this.querySelector('#HeaderDrawerClose').addEventListener('click', () => this.close());
    this.querySelector('#HeaderDrawerCloseMobile').addEventListener('click', () => this.close());

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  close() {
    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  bindEvents() {
    this.querySelectorAll('.summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('.details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('.summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // if (detailsElement.classList.contains('always-opened')) {
    //   event.preventDefault();
    //   const goToUrl = detailsElement.getAttribute('data-new-in-url');
    //   if (goToUrl) {
    //     window.location.href = goToUrl
    //   }
    // }

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      detailsElement.classList.add('menu-opening');
      summaryElement.setAttribute('aria-expanded', true);
      parentMenuElement && parentMenuElement.classList.add('submenu-open');
      !reducedMotion || reducedMotion.matches
        ? addTrapFocus()
        : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    console.log(event);
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('.details').forEach((details) => {
      // details.removeAttribute('open');
      // details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('.details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('.summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('.summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('.details[open]')) {
          trapFocus(detailsElement.closest('.details[open]'), detailsElement.querySelector('.summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden`);

    // if (window.matchMedia('(max-width: 749px)').matches && window.location.pathname == "/account" ) {
    //   console.log('> 750')
    //   const header = document.querySelector('.section-header.shopify-section-group-header-group');
    //   header.style.zIndex = '3'
    // }
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    // if (window.matchMedia('(max-width: 749px)').matches && window.location.pathname == "/account" ) {
    //   console.log('> 750')

    //     const header = document.querySelector('.section-header.shopify-section-group-header-group');
    //     header.style.zIndex = '1'
    // }
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    // removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponentVertical extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    setTimeout(() => {
      this.setSlidePosition(0);
    }, 400);
    setTimeout(() => {
      this.classList.add('loaded');
      this.update();
    }, 600);

    let isDown = false;
    let startX;
    let scrollTop;

    this.slider.addEventListener('mousedown', (e) => {
      isDown = true;
      this.slider.classList.add('active');
      this.slider.classList.remove('move');
      startX = e.pageX - this.slider.offsetTop;
      scrollTop = this.slider.scrollTop;
    });
    this.slider.addEventListener('mouseleave', () => {
      isDown = false;
      this.slider.classList.remove('active');
      this.slider.classList.remove('move');
    });
    this.slider.addEventListener('mouseup', () => {
      isDown = false;
      this.slider.classList.remove('active');
      this.slider.classList.remove('move');
    });
    this.slider.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      this.slider.classList.add('move');
      const x = e.pageX - this.slider.offsetTop;
      const walk = (x - startX) * 1;
      this.slider.scrollTop = scrollTop - walk;
    });

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - this.sliderItemsToShow[0].offsetTop;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetTop) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.slider.addEventListener('scroll', this.handleScroll.bind(this));

    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollTop / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollTop === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  handleScroll() {
    const isAtBottom = this.slider.scrollHeight - this.slider.scrollTop === this.slider.clientHeight;

    if (isAtBottom) {
      this.onAttemptToSlidePastEnd();
    }
  }

  onAttemptToSlidePastEnd() {
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      const elementoDestino = document.querySelector('.product__info-wrapper');
      const posicionY = elementoDestino.offsetTop - 100;

      window.scrollTo({
        top: posicionY,
        behavior: 'smooth',
      });
    }, 0);

    setTimeout(() => {
      document.body.style.overflow = '';
    }, 1000);
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollTop - offset;
    return element.offsetTop + element.clientWidth <= lastVisibleSlide && element.offsetTop >= this.slider.scrollTop;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollTop + step * this.sliderItemOffset
        : this.slider.scrollTop - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      top: position,
    });
  }
}

customElements.define('slider-component-vertical', SliderComponentVertical);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.isSliderDesktop = this.slider.getAttribute('data-is-slider-desktop') === 'true';
    this.isSliderMobile = this.slider.getAttribute('data-is-slider-mobile') === 'true';
    this.enableSliderLooping = this.slider.getAttribute('data-loop') === 'true';
    this.enableAutoplay = this.slider.getAttribute('data-autoplay') === 'true';
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    setTimeout(() => {
      this.setSlidePosition();
    }, 400);

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    if (this.enableAutoplay) this.setAutoPlay();
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  setAutoPlay() {
    if (!this.enableAutoplay) return;

    const isMobileDevice = window.innerWidth <= 749;
    const isDesktopDevice = window.innerWidth > 750;

    if ((isDesktopDevice && this.isSliderDesktop) || (isMobileDevice && this.isSliderMobile)) {
      // console.log('Iniciando autoplay');
      this.autoplaySpeed = this.slider.dataset.speed * 1000;
      this.autoplayInterval = setInterval(() => {
        this.autoRotateSlides();
      }, this.autoplaySpeed);
    } else {
      console.log('Autoplay deshabilitado: No coincide con el dispositivo');
    }
  }

  autoRotateSlides() {
    console.log('autorotate slides');
    if (this.currentPage === this.totalPages) {
      if (this.enableSliderLooping) {
        this.setSlidePosition(0);
        this.currentPage = 1;
      } else {
        clearInterval(this.autoplayInterval);
      }
      return;
    }

    const slideScrollPosition = this.slider.scrollLeft + this.sliderItemOffset;
    this.setSlidePosition(slideScrollPosition);
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    //if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      if (this.enableSliderLooping) {
        this.prevButton.setAttribute('data-first', 'true');
      } else {
        this.prevButton.setAttribute('disabled', 'disabled');
      }
    } else {
      if (this.enableSliderLooping) {
        this.prevButton.removeAttribute('data-first');
      } else {
        this.prevButton.removeAttribute('disabled');
      }
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      if (this.enableSliderLooping) {
        this.nextButton.setAttribute('data-last', 'true');
      } else {
        this.nextButton.setAttribute('disabled', 'disabled');
      }
    } else {
      if (this.enableSliderLooping) {
        this.nextButton.removeAttribute('data-last');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    if (event.currentTarget.dataset.last === 'true') {
      this.setSlidePosition(0);
    } else if (event.currentTarget.dataset.first === 'true') {
      this.setSlidePosition(this.slider.offsetWidth * 5);
    } else {
      const step = event.currentTarget.dataset.step || 1;
      this.slideScrollPosition =
        event.currentTarget.name === 'next'
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      this.setSlidePosition(this.slideScrollPosition);
    }
  }

  setSlidePosition(position) {
    if (this.enableAutoplay) {
      this.smoothScrollTo(this.slider, position, 1500);
    } else {
      this.slider.scrollTo({
        left: position,
      });
    }
  }

  smoothScrollTo(element, targetPosition, duration) {
    const startPosition = element.scrollLeft;
    const distance = targetPosition - startPosition;
    let startTime = null;

    function animation(currentTime) {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;

      element.scrollLeft = startPosition + distance * ease;

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    }

    requestAnimationFrame(animation);
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion?.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
    this.addEventListener('click', (event) => {
      const target = this.getInputForEventTarget(event.target);
      if (!target.classList.contains('disabled') && window.innerWidth < 750) {
        setTimeout(() => {
          this.closest('.product-form__container').querySelector('.product-form__submit').click();
        }, 500);
      } else if (window.innerWidth < 750) {
        setTimeout(() => {
          this.closest('.product-form__container').querySelector('#BIS_trigger').click();
        }, 500);
      }
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    const checkInterval = setInterval(() => {
      if (this.dataset.productId) {
        clearInterval(checkInterval); // Detiene el intervalo
        this.initializeRecommendations(this.dataset.productId); // Llama a la función
      }
    }, 100);
    // this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        console.log('text222', this.dataset.sectionId);
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        productCardsSizes();
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

document.addEventListener('DOMContentLoaded', function () {
  setTimeout(() => {
    const buttonsWishlist = document.querySelectorAll('wishlist-button-block');
    if (buttonsWishlist) {
      buttonsWishlist.forEach((button) => {
        button.addEventListener('click', function () {
          if (button.querySelector('.wk-button').classList.contains('wk-selected')) {
            document.querySelector('.header__icon--cart').classList.add('blinking-text');
            setTimeout(() => {
              document.querySelector('.header__icon--cart').classList.remove('blinking-text');
            }, 3000);
          }
        });
      });
    }

    const buttonsWishlistCollection = document.querySelectorAll('wishlist-button-collection');
    if (buttonsWishlistCollection) {
      buttonsWishlistCollection.forEach((button) => {
        button.addEventListener('click', function () {
          if (button.querySelector('.wk-button').classList.contains('wk-selected')) {
            document.querySelector('.header__icon--cart').classList.add('blinking-text');
            setTimeout(() => {
              document.querySelector('.header__icon--cart').classList.remove('blinking-text');
            }, 3000);
          }
        });
      });
    }
  }, 1000);
});

function viewItemList(paginationElements, page = '1') {
  const resultsContainer = document.querySelector('.product-grid');
  let allResults = [];
  if (paginationElements != null) {
    allResults = paginationElements;
  } else {
    allResults = resultsContainer?.querySelectorAll('.grid__item');
  }

  const url = document.location.href;
  const urlObj = new URL(url);

  const getParamValues = (param) => {
    return [...new URLSearchParams(urlObj.search).entries()]
      .filter(([key, value]) => key.includes(param))
      .map(([key, value]) => value);
  };

  const color = getParamValues('filter.v.option.color');
  const size = getParamValues('filter.v.option.talla');
  const minPrice = urlObj.searchParams.get('filter.v.price.gte');
  const maxPrice = urlObj.searchParams.get('filter.v.price.lte');
  const orderBy = urlObj.searchParams.get('sort_by');

  let filters = [];

  if (color.length > 0) filters.push(`color:${color.join(',')}`);
  if (size.length > 0) filters.push(`talla:${size.join(',')}`);
  if (minPrice !== null || maxPrice !== null) {
    let priceRange = `${minPrice || '0'}-${maxPrice || '∞'}`;
    filters.push(`precio:${priceRange}`);
  }
  if (orderBy !== null) filters.push(`ordenar por:${orderBy}`);

  const viewListId = `${document.title.split(' | ')[0]}|F:${filters.length > 0 ? filters.join(',') : 'N/A'}`;

  let viewItems = [];
  if (allResults?.length > 0) {
    allResults?.forEach((item, index) => {
      item.setAttribute('data-index-list', index + 1);
      item.setAttribute('data-list-page', page);
      const productPrice = item.getAttribute('data-product-price');
      const priceRegularElem = item.getAttribute('data-old-price');
      const priceRegular = parseFloat(priceRegularElem / 100);

      const discount = parseFloat(priceRegularElem) - parseFloat(productPrice);
      let itemCategory2 = '';
      const typeOfProduct = item.getAttribute('data-product-category');
      if (
        typeOfProduct == 'Anillos' ||
        typeOfProduct == 'Collares' ||
        typeOfProduct == 'Pendientes' ||
        typeOfProduct == 'Pulseras'
      ) {
        itemCategory2 = 'Bisutería';
      } else if (
        typeOfProduct == 'Bolsos' ||
        typeOfProduct == 'Bufandas' ||
        typeOfProduct == 'Calcetines' ||
        typeOfProduct == 'Calzado' ||
        typeOfProduct == 'Cinturones' ||
        typeOfProduct == 'Gafas' ||
        typeOfProduct == 'Gorros' ||
        typeOfProduct == 'Monederos'
      ) {
        itemCategory2 = 'Accesorios';
      } else if (typeOfProduct == 'Abrigos' || typeOfProduct == 'Gabardinas') {
        itemCategory2 = 'Abrigos y Gabardinas';
      } else if (typeOfProduct == 'Bañador' || typeOfProduct == 'Baño') {
        itemCategory2 = 'Baño';
      } else if (typeOfProduct == 'Blusas' || typeOfProduct == 'Camisas') {
        itemCategory2 = 'Camisas y Blusas';
      } else if (typeOfProduct == 'Camisetas') {
        itemCategory2 = 'Camisetas';
      } else if (typeOfProduct == 'Chalecos') {
        itemCategory2 = 'Chalecos';
      } else if (typeOfProduct == 'Kimonos' || typeOfProduct == 'Chaquetas') {
        itemCategory2 = 'Chaquetas y Kimonos';
      } else if (typeOfProduct == 'Faldas') {
        itemCategory2 = 'Faldas';
      } else if (typeOfProduct == 'Jerséis' || typeOfProduct == 'Cárdigans' || typeOfProduct == 'Ponchos') {
        itemCategory2 = 'Jerséis y Cárdigans';
      } else if (typeOfProduct == 'Pantalones') {
        itemCategory2 = 'Pantalones';
      } else if (typeOfProduct == 'Sudaderas') {
        itemCategory2 = 'Sudaderas';
      } else if (typeOfProduct == 'Tops') {
        itemCategory2 = 'Tops';
      } else if (typeOfProduct == 'Vaqueros') {
        itemCategory2 = 'Vaqueros';
      } else if (typeOfProduct == 'Vestidos' || typeOfProduct == 'Monos') {
        itemCategory2 == 'Vestidos y Monos';
      }

      let itemCategory = '';
      if (itemCategory2 == 'Accesorios') {
        itemCategory = 'Accesorios';
      } else if (itemCategory2 == 'Bisutería') {
        itemCategory = 'Bisutería';
      } else {
        itemCategory = 'Ropa';
      }

      if (item.getAttribute('data-product-id') != null) {
        viewItems.push({
          item_id: item.getAttribute('data-product-id'),
          item_name: item.getAttribute('data-product-title'),
          quantity: 1,
          price: parseFloat(item.getAttribute('data-product-price') / 100),
          discount: parseFloat(discount / 100) > 0 ? parseFloat(discount / 100) : 0,
          coupon: '',
          item_brand: 'renatta&go',
          item_category: itemCategory,
          item_category2: itemCategory2,
          item_category3: item.getAttribute('data-product-category'),
          item_variant: item.getAttribute('data-product-variant'),
          affiliation: '',
          location_id: '',
          index: index + 1,
          item_list_name: viewListId,
          item_list_id: document.title.split(' | ')[0],
          promotion_name: sessionStorage.getItem('promotion_name') || '',
          promotion_id: sessionStorage.getItem('promotion_id') || '',
          img_articulo: item.getAttribute('data-product-image'),
          color: item.getAttribute('data-product-colors'),
          precio_articulo: parseFloat(item.getAttribute('data-product-price') / 100),
          descuento_artículo: parseFloat(discount / 100) > 0 ? parseFloat(discount / 100) : 0,
          stock: item.getAttribute('data-product-score-stock'),
          tallas_disponibles: getAvailableAndUnavailableSizes(item.getAttribute('data-product-score-stock')).available,
          tallas_no_disponibles: getAvailableAndUnavailableSizes(item.getAttribute('data-product-score-stock'))
            .unavailable,
          score_stock: calcularScoreStock(item.getAttribute('data-product-score-stock')),
          modelo_artículo: item.getAttribute('data-product-model').replace('*', ''),
        });
      }
    });
  }

  // Poner el push al dataLayer fuera del forEach
  setTimeout(() => {
    // window.dataLayer.push({ ecommerce: null });
    // window.dataLayer.push({
    //   'event': 'view_item_list',
    //   ecommerce: {
    //     'item_list_page': page,
    //     'item_list_name': 'collection',
    //     items: [...viewItems]
    //   }
    // });
    gtag('event', 'view_item_list', {
      event: 'view_item_list',
      item_list_page: page,
      currency: 'EUR',
      event_category: 'ecommerce',
      item_list_name: viewListId,
      item_list_id: document.title.split(' | ')[0],
      items: [...viewItems],
    });
  }, 1000);
}

if (window.location.href.includes('/collections')) {
  document.addEventListener('DOMContentLoaded', () => {
    viewItemList();
  });
}

function cardLink(event) {
  event.preventDefault();
  const item = event.target.closest('.grid__item');

  const url = document.location.href;
  const urlObj = new URL(url);

  const getParamValues = (param) => {
    return [...new URLSearchParams(urlObj.search).entries()]
      .filter(([key, value]) => key.includes(param))
      .map(([key, value]) => value);
  };

  const color = getParamValues('filter.v.option.color');
  const size = getParamValues('filter.v.option.talla');
  const minPrice = urlObj.searchParams.get('filter.v.price.gte');
  const maxPrice = urlObj.searchParams.get('filter.v.price.lte');
  const orderBy = urlObj.searchParams.get('sort_by');

  let filters = [];

  if (color.length > 0) filters.push(`color:${color.join(',')}`);
  if (size.length > 0) filters.push(`talla:${size.join(',')}`);
  if (minPrice !== null || maxPrice !== null) {
    let priceRange = `${minPrice || '0'}-${maxPrice || '∞'}`;
    filters.push(`precio:${priceRange}`);
  }
  if (orderBy !== null) filters.push(`ordenar por:${orderBy}`);

  const viewListId = `${document.title.split(' | ')[0]}|F:${filters.length > 0 ? filters.join(',') : 'N/A'}`;

  let gridItem = '';
  if (event.target.closest('.grid__item')) {
    gridItem = event.target.closest('.grid__item');
  } else if (event.target.closest('.collage__item')) {
    gridItem = event.target.closest('.collage__item');
  } else {
    gridItem = event.target.closest('.predictive-search__list-item');
  }
  const index = gridItem.getAttribute('data-index-list');
  const page = gridItem.getAttribute('data-list-page');
  const model = gridItem.getAttribute('data-product-model')?.replace('*', '');
  const productPrice = gridItem.getAttribute('data-product-price');
  const priceRegularElem = gridItem.getAttribute('data-old-price');
  const priceRegular = parseFloat(priceRegularElem / 100);

  const discount = parseFloat(priceRegularElem) - parseFloat(productPrice);
  let itemCategory2 = '';
  const typeOfProduct = gridItem.querySelector('[data-product-category]')?.getAttribute('data-product-category');
  if (
    typeOfProduct == 'Anillos' ||
    typeOfProduct == 'Collares' ||
    typeOfProduct == 'Pendientes' ||
    typeOfProduct == 'Pulseras'
  ) {
    itemCategory2 = 'Bisutería';
  } else if (
    typeOfProduct == 'Bolsos' ||
    typeOfProduct == 'Bufandas' ||
    typeOfProduct == 'Calcetines' ||
    typeOfProduct == 'Calzado' ||
    typeOfProduct == 'Cinturones' ||
    typeOfProduct == 'Gafas' ||
    typeOfProduct == 'Gorros' ||
    typeOfProduct == 'Monederos'
  ) {
    itemCategory2 = 'Accesorios';
  } else if (typeOfProduct == 'Abrigos' || typeOfProduct == 'Gabardinas') {
    itemCategory2 = 'Abrigos y Gabardinas';
  } else if (typeOfProduct == 'Bañador' || typeOfProduct == 'Baño') {
    itemCategory2 = 'Baño';
  } else if (typeOfProduct == 'Blusas' || typeOfProduct == 'Camisas') {
    itemCategory2 = 'Camisas y Blusas';
  } else if (typeOfProduct == 'Camisetas') {
    itemCategory2 = 'Camisetas';
  } else if (typeOfProduct == 'Chalecos') {
    itemCategory2 = 'Chalecos';
  } else if (typeOfProduct == 'Kimonos' || typeOfProduct == 'Chaquetas') {
    itemCategory2 = 'Chaquetas y Kimonos';
  } else if (typeOfProduct == 'Faldas') {
    itemCategory2 = 'Faldas';
  } else if (typeOfProduct == 'Jerséis' || typeOfProduct == 'Cárdigans' || typeOfProduct == 'Ponchos') {
    itemCategory2 = 'Jerséis y Cárdigans';
  } else if (typeOfProduct == 'Pantalones') {
    itemCategory2 = 'Pantalones';
  } else if (typeOfProduct == 'Sudaderas') {
    itemCategory2 = 'Sudaderas';
  } else if (typeOfProduct == 'Tops') {
    itemCategory2 = 'Tops';
  } else if (typeOfProduct == 'Vaqueros') {
    itemCategory2 = 'Vaqueros';
  } else if (typeOfProduct == 'Vestidos' || typeOfProduct == 'Monos') {
    itemCategory2 == 'Vestidos y Monos';
  }

  let itemCategory = '';
  if (itemCategory2 == 'Accesorios') {
    itemCategory = 'Accesorios';
  } else if (itemCategory2 == 'Bisutería') {
    itemCategory = 'Bisutería';
  } else {
    itemCategory = 'Ropa';
  }

  sessionStorage.setItem('item_list_name', viewListId);
  sessionStorage.setItem('item_list_id', document.title.split(' | ')[0]);
  sessionStorage.setItem('index_list', parseInt(index));

  gtag('event', 'select_item', {
    event: 'select_item',
    item_list_page: page,
    currency: 'EUR',
    event_category: 'ecommerce',
    item_list_name: viewListId,
    item_list_id: document.title.split(' | ')[0],
    items: [
      {
        item_id: gridItem.getAttribute('data-product-id'),
        item_name: gridItem.getAttribute('data-product-title'),
        quantity: 1,
        price: parseFloat(gridItem.getAttribute('data-product-price') / 100),
        discount: parseFloat(discount / 100) > 0 ? parseFloat(discount / 100) : 0,
        coupon: '',
        item_brand: 'renatta&go',
        item_category: itemCategory,
        item_category2: itemCategory2,
        item_category3: gridItem.getAttribute('data-product-category'),
        item_variant: gridItem.getAttribute('data-product-variant'),
        affiliation: '',
        location_id: '',
        index: parseInt(index),
        item_list_name: viewListId,
        item_list_id: document.title.split(' | ')[0],
        promotion_name: sessionStorage.getItem('promotion_name') || '',
        promotion_id: sessionStorage.getItem('promotion_id') || '',
        img_articulo: gridItem.getAttribute('data-product-image'),
        color: gridItem.getAttribute('data-product-colors'),
        precio_articulo: parseFloat(gridItem.getAttribute('data-product-price') / 100),
        descuento_artículo: parseFloat(discount / 100) > 0 ? parseFloat(discount / 100) : 0,
        stock: gridItem.getAttribute('data-product-score-stock'),
        tallas_disponibles: getAvailableAndUnavailableSizes(gridItem.getAttribute('data-product-score-stock'))
          .available,
        tallas_no_disponibles: getAvailableAndUnavailableSizes(gridItem.getAttribute('data-product-score-stock'))
          .unavailable,
        score_stock: calcularScoreStock(gridItem.getAttribute('data-product-score-stock')),
        modelo_artículo: model,
      },
    ],
  });

  setTimeout(() => {
    window.location.href = event.target.href;
  }, 700);
}

function calcularScoreStock(listado) {
  // Parsear el listado
  let tallas = listado?.split(',').map((item) => {
    let [talla, stock] = item.split('|');
    return { talla, stock: parseInt(stock, 10) };
  });

  // Separar tallas con y sin stock
  let conStock = tallas?.filter((t) => t.stock > 0);
  let sinStock = tallas?.filter((t) => t.stock === 0);

  // Si no hay stock en ninguna talla, el score es 0
  if (conStock?.length === 0) return 0;

  // Si solo una tiene stock, devolver ese stock (como en la lógica original)
  if (conStock?.length === 1 && sinStock?.length > 0) {
    return conStock[0]?.stock;
  }

  // Calcular total de stock (de las que sí tienen)
  let totalStock = conStock?.reduce((acc, t) => acc + t.stock, 0);

  let score = 0;

  // Tallas con stock: 85% del peso total
  conStock?.forEach((t) => {
    let proporcion = (t.stock / totalStock) * 0.85;
    score += proporcion * t.stock;
  });

  // Tallas sin stock: 15% dividido entre ellas
  if (sinStock?.length > 0) {
    let proporcionFija = 0.15 / sinStock.length;
    sinStock.forEach((t) => {
      score += proporcionFija * 0; // stock = 0, pero pueden usarse para penalizar si se quiere
    });
  }

  return score.toFixed(2);
}

function getAvailableAndUnavailableSizes(list) {
  // Convert the list into an array of objects { size, stock }
  let sizes = list?.split(',').map((item) => {
    let [size, stock] = item.split('|');
    return { size, stock: parseInt(stock, 10) };
  });

  // Separate available and unavailable sizes
  let availableSizes = sizes?.filter((s) => s.stock > 0).map((s) => s.size);
  let unavailableSizes = sizes?.filter((s) => s.stock <= 0).map((s) => s.size);

  return {
    available: availableSizes?.join(','),
    unavailable: unavailableSizes?.join(','),
  };
}

function createWishlistFeedbackSuccess(button) {
  document.querySelector('.wishlist__feedback')?.remove();

  const wishlistFeedbackSuccess = document.createElement('div');
  wishlistFeedbackSuccess.className = 'wishlist__feedback wishlist__success';

  const wishlistFeedbackWrapperSuccess = document.createElement('div');
  wishlistFeedbackWrapperSuccess.className = 'wishlist__feedback--wrapper';

  const wishlistFeedbackSuccessLabelAdd = window.wishlistString.add;
  const wishlistFeedbackSuccessLabelLink = window.wishlistString.link;

  wishlistFeedbackWrapperSuccess.innerHTML =
    wishlistFeedbackSuccessLabelAdd +
    `<a href="/" class="wishlist__link link" onclick="event.preventDefault();document.querySelector('.header__icon--cart').click();document.querySelector('.tablink.tablink-wishlist').click();">` +
    wishlistFeedbackSuccessLabelLink +
    `</a>
  `;

  wishlistFeedbackSuccess.appendChild(wishlistFeedbackWrapperSuccess);

  button.target.closest('.wk-button')?.closest('body')?.prepend(wishlistFeedbackSuccess);

  loadWishlist();

  setTimeout(() => {
    wishlistFeedbackSuccess.remove();
  }, 3000);
}

function loadWishlist() {
  document.querySelectorAll('wishlist-button-block .wk-button')?.forEach((button) => {
    console.log(button);
    button.addEventListener('click', (btn) => {
      console.log(btn.target.classList);
      if (!btn.target.classList.contains('wk-selected')) {
        createWishlistFeedbackSuccess(btn);
      }
      setTimeout(() => {
        loadWishlist();
      }, 1000);
    });
  });
}

document.addEventListener('DOMContentLoaded', function () {
  const checkWishlistLoaded = setInterval(() => {
    if (typeof WishlistKing !== 'undefined') {
      clearInterval(checkWishlistLoaded);
      setTimeout(() => {
        console.log('LOAD WISHLIST');
        loadWishlist();
      }, 1000);
    }
  }, 100);
});
